<template>
  <span
    ref="triggerEl"
    v-bind="$attrs"
    class="inline-flex"
    @click.prevent="toggleClick"
    @mouseenter="enableVisibility"
    @mouseleave="disableVisibility"
  >
    <slot />
  </span>

  <ClientOnly v-if="!disabled">
    <component
      :is="modalComponent"
      v-if="isMobile"
      v-model:visible="visible"
      :title="title"
      :label-primary="buttonText || t('actions.gotIt')"
      @action:primary="(dismiss) => dismiss()"
    >
      <slot name="content">
        {{ content }}
      </slot>
    </component>

    <div
      v-else-if="visible"
      ref="floatingEl"
      :class="computedClasses"
      :style="floatingStyles"
    >
      <slot name="content">
        {{ content }}
      </slot>

      <div
        ref="floatingArrowEl"
        :class="arrowComputedClasses"
        :style="arrowComputedStyles"
      />
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import type { TooltipModalType, TooltipPlacement } from './types'
import { TModalCritical, TModalInfo } from '#components'

const props = withDefaults(defineProps<{
  content?: string
  title: string
  buttonText?: string
  placement?: TooltipPlacement
  disabled?: boolean
  modalType?: TooltipModalType
}>(), {
  placement: 'bottom',
  modalType: 'info',
})

defineOptions({
  inheritAttrs: false,
})

const modalComponent = computed(() => {
  return props.modalType === 'critical' ? TModalCritical : TModalInfo
})

const { t } = useI18n()

const computedClasses = computed(() => ([
  'max-w-xs bg-primary-950 text-white text-sm px-4 py-2 rounded z-20',
]))

const { isMobile } = useDevice()
const visible = ref(false)

const triggerEl = ref<HTMLSpanElement>()
const floatingEl = ref<HTMLDivElement>()
const floatingArrowEl = ref<HTMLDivElement>()
const { floatingStyles, middlewareData } = useFloating(triggerEl, floatingEl, {
  placement: props.placement,
  middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: floatingArrowEl, padding: 8 })],
  whileElementsMounted: autoUpdate,
})

const arrowComputedClasses = computed(() => ([
  'size-2 bg-primary-950 absolute rotate-45',
  {
    'right-0 translate-x-1/2': props.placement === 'left',
    'left-0 -translate-x-1/2': props.placement === 'right',
    'bottom-0 translate-y-1/2': props.placement === 'top',
    'top-0 -translate-y-1/2': props.placement === 'bottom',
  },
]))

const arrowComputedStyles = computed(() => {
  return {
    left: middlewareData.value.arrow?.x != null ? `${middlewareData.value.arrow.x}px` : '',
    top: middlewareData.value.arrow?.y != null ? `${middlewareData.value.arrow.y}px` : '',
  }
})

function disableVisibility() {
  if (!isMobile) {
    visible.value = false
  }
}

function enableVisibility() {
  if (!isMobile && !props.disabled) {
    visible.value = true
  }
}

function toggleClick() {
  if (isMobile && !props.disabled) {
    visible.value = !visible.value
  }
}
</script>
